import { useApolloClient, useQuery } from "@apollo/client";
import CustomExportButton from "@app.automotus.io/components/CustomExportButton";
import ManualApplyGridFilterPanel from "@app.automotus.io/components/ManualApplyGridFilterPanel";
import {
  GET_POLICY_VEHICLE_EXEMPTIONS,
  GetPolicyVehicleExemptionsData,
  GetPolicyVehicleExemptionsFilterCondition,
  GetPolicyVehicleExemptionsVars,
  GetPolicyVehicleExemptionsWhereFilter,
  PolicyVehicleExemptionsOrderBy,
  PolicyVehicleExemptionSummary,
} from "common/graphql";
import {
  filterModelToSearchParams,
  parseFilterModelFromSearchParams,
  parseSortModel,
  serializeSortModel,
} from "common/graphql/queryString";
import Button from "@mui/material/Button";
import {
  DataTableContainer,
  ACTIONS_COLUMN_ICON,
  ColumnsToolbarButton,
  ResetLayoutButton,
  FilteredColumnsPanel,
  STANDARD_GRID_PROPS,
} from "@app.automotus.io/components/DataTableContainer";
import Stack from "@mui/material/Stack";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import {
  DataGridPro,
  getGridDateOperators,
  getGridStringOperators,
  GridActionsCellItem,
  GridActionsColDef,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridSortModel,
  GridToolbarFilterButton,
  useGridApiContext,
} from "@mui/x-data-grid-pro";
import React from "react";
import { useSearchParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import AddVehicleExemptionDialog from "./AddVehicleExemptionDialog";
import DeleteIcon from "@mui/icons-material/Delete";
import RemoveVehicleExemptionDialog from "./RemoveVehicleExemptionDialog";

export const VehicleExemptionsTable: React.FC = () => {
  const client = useApolloClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [vehicleExemptionToRemove, setVehicleExemptionToRemove] = React.useState<
    PolicyVehicleExemptionSummary | undefined
  >(undefined);

  const TABLE_COLUMNS = React.useMemo(
    () => [
      {
        field: "licensePlate",
        headerName: "License Plate",
        align: "left",
        headerAlign: "left",
        valueGetter({ row }) {
          return `${row.registeredVehicle?.stateCode ?? row.licensePlateStateCodeLike} ${
            row.registeredVehicle?.licensePlate ?? row.licensePlateNumberLike
          }`;
        },
        sortable: false,
        filterOperators: getGridStringOperators().filter((op) => ["equals", "contains"].includes(op.value)),
        width: isMobile ? 120 : 150,
      } as GridColDef<PolicyVehicleExemptionSummary, string>,
      {
        field: "vehicleDescription",
        headerName: "Vehicle Description",
        align: "left",
        headerAlign: "left",
        valueFormatter({ value }) {
          return value ?? "-";
        },
        sortable: false,
        filterable: false,
        ...(isMobile ? { width: 140 } : { flex: 1 }),
      } as GridColDef<PolicyVehicleExemptionSummary, string | null>,
      {
        field: "beginAt",
        headerName: "Exempt Since",
        align: "left",
        headerAlign: "left",
        valueGetter({ row }) {
          return new Date(row.beginAt);
        },
        sortingOrder: ["desc", "asc"],
        filterOperators: getGridDateOperators().filter((op) => ["is", "before", "after"].includes(op.value)),
        ...(isMobile ? { width: 145 } : { flex: 1 }),
        type: isMobile ? "date" : "dateTime",
      } as GridColDef<PolicyVehicleExemptionSummary, Date>,
      {
        ...ACTIONS_COLUMN_ICON,
        getActions: ({ row }) => [
          <GridActionsCellItem
            label={"Remove Exemption"}
            icon={<DeleteIcon />}
            onClick={() => setVehicleExemptionToRemove(row)}
          />,
        ],
      } as GridActionsColDef,
    ],
    [isMobile],
  );

  const [searchParams, setSearchParams] = useSearchParams({
    page: "1",
    sort: "beginAt+desc",
  });

  const page = Number(searchParams.get("page") || 1);
  const handlePageChange = (newPage: number) => {
    const newPageOneIndexed = newPage + 1;

    if (newPageOneIndexed !== page) {
      searchParams.set("page", `${newPageOneIndexed}`);
      setSearchParams(searchParams, { replace: true });
    }
  };

  const PAGE_SIZE = 100;

  const sort = searchParams.get("sort") || "beginAt+desc";
  const sortModel: GridSortModel = React.useMemo(() => parseSortModel(sort), [sort]);
  const handleSortModelChange = (newSortModel: GridSortModel) => {
    searchParams.set("sort", serializeSortModel(newSortModel));
    searchParams.set("page", "1");
    setSearchParams(searchParams, { replace: true });
  };

  const orderBy: PolicyVehicleExemptionsOrderBy[] = React.useMemo(() => {
    const { field: sortField, sort: sortDirection } = sortModel[0];
    switch (sortField) {
      case "beginAt":
        return [{ begin_at: sortDirection || "desc" }];
      default:
        return [{ begin_at: sortDirection || "desc" }];
    }
  }, [sortModel]);

  const appliedFilters = React.useMemo(() => parseFilterModelFromSearchParams(searchParams), [searchParams]);
  const [filterModel, setFilterModel] = React.useState<GridFilterModel | undefined>(appliedFilters);

  const handleApplyFilters = (newFilterModel: GridFilterModel) => {
    searchParams.set("page", "1");
    const filterParams = filterModelToSearchParams(newFilterModel, searchParams);
    setSearchParams(filterParams, { replace: true });
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    if (newFilterModel.items.length === 0 && filterModel?.items?.length) {
      // Manually apply filters when user clears all filters
      handleApplyFilters(newFilterModel);
    }

    if (newFilterModel !== filterModel) {
      setFilterModel(newFilterModel);
    }
  };

  const filter: GetPolicyVehicleExemptionsWhereFilter = React.useMemo(() => {
    const baseFilter: GetPolicyVehicleExemptionsWhereFilter = { end_at: { _is_null: true } };

    if (!appliedFilters) {
      return baseFilter;
    }

    const columnFilters: GetPolicyVehicleExemptionsFilterCondition[] = appliedFilters.items
      .filter((item) => item.columnField === "licensePlate")
      .map((item) => {
        const value = item.value as string;

        if (item.operatorValue === "equals") {
          // Match "CA 1234" — 2-letter state prefix then plate
          const parts = value.match(/^([A-Za-z]{2})\s+(.+)$/);
          if (parts) {
            return {
              _or: [
                { registered_vehicle: { state_code: { _ilike: parts[1] }, license_plate: { _ilike: parts[2] } } },
                {
                  license_plate_state_code_like: { _ilike: parts[1] },
                  license_plate_number_like: { _ilike: parts[2] },
                },
              ],
            };
          }
          return {
            _or: [
              { registered_vehicle: { license_plate: { _ilike: value } } },
              { license_plate_number_like: { _ilike: value } },
            ],
          };
        }

        const spaceIndex = value.indexOf(" ");

        if (spaceIndex > 0 && spaceIndex < value.length - 1) {
          const state = value.slice(0, spaceIndex);
          const plate = value.slice(spaceIndex + 1);
          return {
            _or: [
              { registered_vehicle: { state_code: { _ilike: `%${state}%` }, license_plate: { _ilike: `%${plate}%` } } },
              {
                license_plate_state_code_like: { _ilike: `%${state}%` },
                license_plate_number_like: { _ilike: `%${plate}%` },
              },
            ],
          };
        }

        return {
          _or: [
            { registered_vehicle: { state_code: { _ilike: `%${value}%` } } },
            { registered_vehicle: { license_plate: { _ilike: `%${value}%` } } },
            { license_plate_state_code_like: { _ilike: `%${value}%` } },
            { license_plate_number_like: { _ilike: `%${value}%` } },
          ],
        };
      });

    if (appliedFilters.linkOperator === "and") {
      return { ...baseFilter, _and: columnFilters };
    } else {
      return { ...baseFilter, _or: columnFilters };
    }
  }, [appliedFilters]);

  const { data, loading, error } = useQuery<GetPolicyVehicleExemptionsData, GetPolicyVehicleExemptionsVars>(
    GET_POLICY_VEHICLE_EXEMPTIONS,
    {
      variables: {
        offset: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
        orderBy,
        filter,
      },
      fetchPolicy: "network-only",
    },
  );

  const fetchTotalCount = async () => {
    const result = await client.query<GetPolicyVehicleExemptionsData, GetPolicyVehicleExemptionsVars>({
      query: GET_POLICY_VEHICLE_EXEMPTIONS,
      variables: {
        offset: 0,
        limit: 0,
        orderBy,
        filter,
      },
    });

    return result.data?.policyVehicleExemptionAggregate.aggregate.count || 0;
  };

  const fetchDataBatch = async (offset: number, limit: number) => {
    const result = await client.query<GetPolicyVehicleExemptionsData, GetPolicyVehicleExemptionsVars>({
      query: GET_POLICY_VEHICLE_EXEMPTIONS,
      variables: {
        offset,
        limit,
        orderBy,
        filter,
      },
    });

    return result.data?.policyVehicleExemptions || [];
  };

  return (
    <DataTableContainer
      tableName="vehicle-exemptions-table"
      defaultVisibilityModel={VEHICLE_EXEMPTIONS_DEFAULT_VISIBILITY}
    >
      {({ apiRef, initialState, isIconOnly, resetLayout }) => (
        <>
          <DataGridPro
            apiRef={apiRef}
            key={isMobile ? "mobile" : "desktop"}
            density={isMobile ? "compact" : "standard"}
            localeText={isIconOnly ? { toolbarFilters: "" } : undefined}
            components={{
              Toolbar: TableToolbar,
              FilterPanel: ManualApplyGridFilterPanel,
              ColumnsPanel: FilteredColumnsPanel,
            }}
            componentsProps={{
              filterPanel: {
                onApplyFilters: handleApplyFilters,
              },
              toolbar: {
                fetchTotalCount,
                fetchDataBatch,
                exportFileName: `vehicle_exemptions_${Date.now()}`,
                columns: TABLE_COLUMNS,
                isMobile,
                defaultVisibilityModel: VEHICLE_EXEMPTIONS_DEFAULT_VISIBILITY,
                isIconOnly,
                resetLayout,
              },
            }}
            columns={TABLE_COLUMNS}
            rows={data?.policyVehicleExemptions || []}
            rowCount={data?.policyVehicleExemptionAggregate.aggregate.count || 0}
            loading={loading}
            {...STANDARD_GRID_PROPS}
            error={error}
            pagination
            page={page - 1}
            onPageChange={handlePageChange}
            pageSize={PAGE_SIZE}
            paginationMode={"server"}
            rowsPerPageOptions={[PAGE_SIZE]}
            sortingMode={"server"}
            sortModel={sortModel}
            filterMode={"server"}
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            initialState={initialState}
            onSortModelChange={handleSortModelChange}
            sx={{
              flexGrow: 1,
              ".MuiDataGrid-columnHeaderTitle": {
                fontSize: {
                  xs: "14px",
                  md: "18px",
                },
                fontWeight: {
                  xs: 600,
                  md: 700,
                },
                lineHeight: "24px",
                letterSpacing: {
                  xs: 0.1,
                  md: 0.17,
                },
              },
            }}
          />
          <RemoveVehicleExemptionDialog
            open={!!vehicleExemptionToRemove}
            vehicleExemption={vehicleExemptionToRemove}
            onClose={() => setVehicleExemptionToRemove(undefined)}
          />
        </>
      )}
    </DataTableContainer>
  );
};

const VEHICLE_EXEMPTIONS_DEFAULT_VISIBILITY: GridColumnVisibilityModel = {
  licensePlate: true,
  vehicleDescription: true,
  beginAt: true,
  actions: true,
};

interface TableToolbarProps {
  fetchTotalCount: () => Promise<number>;
  fetchDataBatch: (offset: number, limit: number) => Promise<unknown[]>;
  exportFileName: string;
  columns: GridColDef[];
  isMobile: boolean;
  /** The table's default column visibility — see useHiddenColumnsCount's docs on why this must stay a stable, code-defined constant. */
  defaultVisibilityModel: GridColumnVisibilityModel;
  /** Below 700px, collapses the export button to icon-only. The filter button's label is controlled separately, at the parent DataGridPro's `localeText` prop. */
  isIconOnly: boolean;
  /** Clears this table's saved layout and restores its defaults — see DataTableContainerChildrenParams.resetLayout. */
  resetLayout: () => void;
}

const TableToolbar: React.FC<TableToolbarProps> = ({
  fetchTotalCount,
  fetchDataBatch,
  exportFileName,
  columns,
  isMobile,
  defaultVisibilityModel,
  isIconOnly,
  resetLayout,
}) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const apiRef = useGridApiContext();

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      height={isMobile ? 44 : 52}
      px={isMobile ? 1 : 1.25}
      spacing={1}
    >
      <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
        <GridToolbarFilterButton
          componentsProps={{
            button: {
              sx: {
                fontSize: "16px",
                fontWeight: 400,
                letterSpacing: 0.15,
                textTransform: "none",
                ...(isIconOnly ? { minWidth: "auto", px: 1, "& .MuiButton-startIcon": { mr: 0, ml: 0 } } : {}),
              },
            },
          }}
        />
        <ColumnsToolbarButton apiRef={apiRef} defaultVisibilityModel={defaultVisibilityModel} />
        <CustomExportButton
          getTotalCount={fetchTotalCount}
          fetchDataBatch={fetchDataBatch}
          fileName={exportFileName}
          columns={columns}
          aria-label="Export"
          buttonLabel={isIconOnly ? "" : "Export"}
          sx={{
            fontSize: "16px",
            fontWeight: 400,
            letterSpacing: 0.15,
            textTransform: "none",
            ...(isIconOnly ? { minWidth: "auto", px: 1, "& .MuiButton-startIcon": { mr: 0, ml: 0 } } : {}),
          }}
        />
        <ResetLayoutButton onReset={resetLayout} isIconOnly={isIconOnly} />
      </Stack>
      <Button variant="contained" size={isMobile ? "small" : "medium"} onClick={() => setDialogOpen(true)}>
        <AddIcon fontSize={isMobile ? "small" : "medium"} />
      </Button>
      <AddVehicleExemptionDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </Stack>
  );
};

export default VehicleExemptionsTable;
